/**
 * AudioStudioPanel — script editor + audio mode picker for UGC Studio.
 *
 * Used in two contexts:
 *  - At create-time: caller passes onChange and reads `script` / `audioMode`
 *    state to forward into the createJob payload.
 *  - At remix-time: caller passes a completed `jobId` and we hit the
 *    /jobs/:id/remix-audio endpoint directly.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Wand2, Mic2, Music2, FileAudio2, Volume2, Square } from "lucide-react";
import {
  ugcStudioApi,
  type AudioMode,
  type Script,
  type ScriptBeat,
  type UgcFormatId,
} from "./api";
import { VoiceManager } from "./voice-manager";
import { evaluateScriptFit, type ScriptDurationFit } from "./script-duration";
import { useUgcLight } from "./ugc-theme";

interface ScriptComposerProps {
  value: Script;
  onChange: (next: Script) => void;
  onAutoFill: (brief?: string) => void;
  isAutoFilling: boolean;
  videoSeconds: number;
}

const AUDIO_MODE_OPTIONS: Array<{
  id: AudioMode;
  labelKey: string;
  descKey: string;
  icon: typeof Mic2;
}> = [
  {
    id: "native",
    labelKey: "ugcStudio.audio.modeNativeLabel",
    descKey: "ugcStudio.audio.modeNativeDesc",
    icon: Music2,
  },
  {
    id: "overlay",
    labelKey: "ugcStudio.audio.modeOverlayLabel",
    descKey: "ugcStudio.audio.modeOverlayDesc",
    icon: FileAudio2,
  },
  {
    id: "lipsync",
    labelKey: "ugcStudio.audio.modeLipsyncLabel",
    descKey: "ugcStudio.audio.modeLipsyncDesc",
    icon: Mic2,
  },
  {
    id: "voiceover",
    labelKey: "ugcStudio.audio.modeVoiceoverLabel",
    descKey: "ugcStudio.audio.modeVoiceoverDesc",
    icon: Music2,
  },
];

export interface AudioStudioState {
  audioMode: AudioMode;
  script: Script;
}

interface VoicePreviewButtonProps {
  avatarId?: string | null;
  voiceId?: string | null;
  /** Optional small label override; default "Posłuchaj głosu". */
  label?: string;
}

/**
 * Plays ElevenLabs' free hosted voice sample. Click again to stop.
 * Caches the sample URL per (avatarId | voiceId) so subsequent clicks are instant.
 */
export function VoicePreviewButton(props: VoicePreviewButtonProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [cachedUrl, setCachedUrl] = useState<string | null>(null);

  const fetchMut = useMutation({
    mutationFn: () =>
      ugcStudioApi.previewVoice({
        avatarId: props.avatarId ?? undefined,
        voiceId: props.voiceId ?? undefined,
      }),
    onSuccess: (res) => {
      setCachedUrl(res.previewUrl);
      playUrl(res.previewUrl);
    },
    onError: (err: Error) =>
      toast({
        title: t("ugcStudio.audio.samplePlaybackFailed"),
        description: err.message,
        variant: "destructive",
      }),
  });

  function playUrl(url: string): void {
    stop();
    const audio = new Audio(url);
    audio.onended = () => setIsPlaying(false);
    audio.onerror = () => {
      setIsPlaying(false);
      toast({ title: t("ugcStudio.audio.playbackError"), variant: "destructive" });
    };
    audioRef.current = audio;
    audio
      .play()
      .then(() => setIsPlaying(true))
      .catch((err) => {
        setIsPlaying(false);
        toast({
          title: t("ugcStudio.audio.playbackBlocked"),
          description: String(err.message ?? err),
          variant: "destructive",
        });
      });
  }

  function stop(): void {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    setIsPlaying(false);
  }

  function handleClick(): void {
    if (isPlaying) return stop();
    if (cachedUrl) return playUrl(cachedUrl);
    fetchMut.mutate();
  }

  // Reset cache if avatar/voice context changed.
  useEffect(() => {
    setCachedUrl(null);
    stop();
  }, [props.avatarId, props.voiceId]);

  // Cleanup on unmount.
  useEffect(() => () => stop(), []);

  const Icon = isPlaying ? Square : Volume2;
  const label = props.label ?? (isPlaying ? "Stop" : t("ugcStudio.audio.listenVoice"));

  // Server requires either avatarId or voiceId — hide button if neither is set.
  if (!props.avatarId && !props.voiceId) return null;

  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      onClick={handleClick}
      disabled={fetchMut.isPending}
      data-testid="voice-preview"
    >
      {fetchMut.isPending ? (
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      ) : (
        <Icon className="mr-2 h-4 w-4" />
      )}
      {fetchMut.isPending ? t("ugcStudio.audio.loading") : label}
    </Button>
  );
}

interface AudioStudioPanelProps {
  /** Required to fetch a script preview (calls scrip generator with this context). */
  format: UgcFormatId;
  durationSeconds: number;
  avatarId?: string | null;
  productId?: string | null;
  userBrief?: string;
  /** Disable the entire panel (e.g. during job submission). */
  disabled?: boolean;

  /** Controlled state. */
  audioMode: AudioMode;
  script: Script;
  onAudioModeChange: (next: AudioMode) => void;
  onScriptChange: (next: Script) => void;
  /** Custom voice override; null = use avatar default. */
  voiceId?: string | null;
  onVoiceChange?: (voiceId: string | null) => void;
}

export function AudioStudioPanel(props: AudioStudioPanelProps) {
  const L = useUgcLight();
  const { t } = useTranslation();
  const { toast } = useToast();
  const previewMut = useMutation({
    mutationFn: () =>
      ugcStudioApi.previewScript({
        format: props.format,
        durationSeconds: props.durationSeconds,
        avatarId: props.avatarId ?? null,
        productId: props.productId ?? null,
        userBrief: props.userBrief?.trim() || "",
      }),
    onSuccess: (res) => {
      props.onScriptChange(res.script);
      toast({
        title: t("ugcStudio.audio.scriptGenerated"),
        description: t("ugcStudio.audio.scriptGeneratedDesc", { count: res.script.length }),
      });
    },
    onError: (err: Error) =>
      toast({
        title: t("ugcStudio.audio.generateScriptFailed"),
        description: err.message,
        variant: "destructive",
      }),
  });

  // Initialise an empty beat row when the user enables an audio mode for the first time.
  useEffect(() => {
    if (props.audioMode !== "native" && props.script.length === 0) {
      const fallback = createEmptyScript(props.durationSeconds);
      props.onScriptChange(fallback);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.audioMode]);

  const showScript = props.audioMode !== "native";

  return (
    <div className={`space-y-4 rounded-lg border p-4 ${L ? "border-gray-200" : "border-zinc-800"}`}>
      <div className="flex items-center justify-between">
        <div>
          <Label className={`text-sm font-semibold ${L ? "text-gray-900" : "text-zinc-100"}`}>
            {t("ugcStudio.audio.label")}
          </Label>
          <p className={`text-xs mt-1 ${L ? "text-gray-500" : "text-zinc-400"}`}>
            {t("ugcStudio.audio.subtitle")}
          </p>
        </div>
        {showScript ? (
          <VoicePreviewButton avatarId={props.avatarId} voiceId={props.voiceId ?? null} />
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {AUDIO_MODE_OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const isActive = props.audioMode === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              disabled={props.disabled}
              onClick={() => props.onAudioModeChange(opt.id)}
              className={[
                "flex items-start gap-3 rounded-md border p-3 text-left transition-colors",
                isActive
                  ? "border-orange-500 bg-orange-500/10"
                  : L
                    ? "border-gray-200 bg-gray-50 hover:border-gray-300"
                    : "border-zinc-800 bg-zinc-900/40 hover:border-zinc-700",
              ].join(" ")}
              data-testid={`audio-mode-${opt.id}`}
            >
              <Icon
                className={`h-5 w-5 shrink-0 ${isActive ? "text-orange-400" : L ? "text-gray-400" : "text-zinc-400"}`}
              />
              <div className="space-y-0.5">
                <div
                  className={`text-sm font-medium ${isActive ? (L ? "text-orange-700" : "text-orange-100") : L ? "text-gray-900" : "text-zinc-100"}`}
                >
                  {t(opt.labelKey)}
                </div>
                <div className={`text-xs ${L ? "text-gray-500" : "text-zinc-400"}`}>
                  {t(opt.descKey)}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {showScript ? (
        <>
          {props.onVoiceChange ? (
            <VoiceManager
              value={props.voiceId ?? null}
              onChange={props.onVoiceChange}
              disabled={props.disabled}
            />
          ) : null}
          <ScriptComposer
            value={props.script}
            onChange={props.onScriptChange}
            onAutoFill={() => previewMut.mutate()}
            isAutoFilling={previewMut.isPending}
            videoSeconds={props.durationSeconds}
          />
        </>
      ) : null}
    </div>
  );
}

function ScriptComposer(props: ScriptComposerProps) {
  const L = useUgcLight();
  const { t } = useTranslation();
  const fit = evaluateScriptFit(props.value, props.videoSeconds);
  return (
    <div className={`space-y-3 border-t pt-4 ${L ? "border-gray-200" : "border-zinc-800"}`}>
      <div className="flex items-center justify-between">
        <div>
          <Label className={`text-sm font-semibold ${L ? "text-gray-900" : "text-zinc-100"}`}>
            {t("ugcStudio.audio.scriptLabel")}
          </Label>
          <p className={`text-[11px] mt-0.5 ${L ? "text-gray-500" : "text-zinc-500"}`}>
            {t("ugcStudio.audio.scriptHint")}
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => props.onAutoFill()}
          disabled={props.isAutoFilling}
          data-testid="script-autofill"
        >
          {props.isAutoFilling ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Wand2 className="mr-2 h-4 w-4" />
          )}
          {props.isAutoFilling
            ? t("ugcStudio.audio.generating")
            : t("ugcStudio.audio.generateSuggestion")}
        </Button>
      </div>

      <ScriptDurationBadge fit={fit} />

      {props.value.length === 0 ? (
        <p className={`text-xs ${L ? "text-gray-500" : "text-zinc-500"}`}>
          {t("ugcStudio.audio.noBeats")}
        </p>
      ) : (
        <div className="space-y-2">
          {props.value.map((beat, idx) => (
            <BeatRow
              key={idx}
              beat={beat}
              onChange={(next) => updateBeat(props.value, idx, next, props.onChange)}
              onRemove={() => removeBeat(props.value, idx, props.onChange)}
            />
          ))}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="text-xs"
            onClick={() => addBeat(props.value, props.onChange)}
          >
            {t("ugcStudio.audio.addBeat")}
          </Button>
        </div>
      )}
    </div>
  );
}

interface BeatRowProps {
  beat: ScriptBeat;
  onChange: (next: ScriptBeat) => void;
  onRemove: () => void;
}

function BeatRow({ beat, onChange, onRemove }: BeatRowProps) {
  const L = useUgcLight();
  const { t } = useTranslation();
  return (
    <div
      className={`grid grid-cols-[80px_80px_1fr_auto] items-start gap-2 rounded-md border p-2 ${L ? "border-gray-200 bg-gray-50" : "border-zinc-800 bg-zinc-900/40"}`}
    >
      <Input
        type="number"
        step="0.5"
        min={0}
        value={beat.start}
        onChange={(e) => onChange({ ...beat, start: Number(e.target.value) || 0 })}
        className="h-8 text-xs"
        aria-label="Start (s)"
      />
      <Input
        type="number"
        step="0.5"
        min={0}
        value={beat.end}
        onChange={(e) => onChange({ ...beat, end: Number(e.target.value) || 0 })}
        className="h-8 text-xs"
        aria-label={t("ugcStudio.audio.beatEndLabel")}
      />
      <Textarea
        value={beat.text}
        onChange={(e) => onChange({ ...beat, text: e.target.value })}
        className="min-h-[60px] text-sm"
        placeholder={t("ugcStudio.audio.beatPlaceholder")}
      />
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={onRemove}
        className={`h-8 px-2 text-xs ${L ? "text-gray-500" : "text-zinc-500"}`}
      >
        ×
      </Button>
    </div>
  );
}

// ── Pure helpers ────────────────────────────────────────────

function ScriptDurationBadge({ fit }: { fit: ScriptDurationFit }) {
  const { t } = useTranslation();
  const L = useUgcLight();
  const palette: Record<ScriptDurationFit["status"], string> = {
    empty: L
      ? "border-gray-200 bg-gray-100 text-gray-500"
      : "border-zinc-800 bg-zinc-900/40 text-zinc-400",
    fits: "border-emerald-700/40 bg-emerald-900/20 text-emerald-200",
    overrun: "border-red-700/50 bg-red-900/30 text-red-200",
    underrun: "border-amber-700/40 bg-amber-900/20 text-amber-200",
  };
  const estimated = `${fit.estimatedSeconds.toFixed(1)}s`;
  const hint =
    fit.status === "empty"
      ? t("ugcStudio.scriptDuration.empty")
      : t(`ugcStudio.scriptDuration.${fit.status}`, { estimated, videoSeconds: fit.videoSeconds });
  return (
    <div
      className={`rounded-md border px-3 py-2 text-[11px] leading-snug ${palette[fit.status]}`}
      data-testid="script-duration-badge"
    >
      {hint}
    </div>
  );
}

function createEmptyScript(totalDuration: number): Script {
  return [{ start: 0, end: Math.min(totalDuration, 5), text: "" }];
}

function updateBeat(
  script: Script,
  idx: number,
  next: ScriptBeat,
  onChange: (s: Script) => void
): void {
  const copy = [...script];
  copy[idx] = next;
  onChange(copy);
}

function removeBeat(script: Script, idx: number, onChange: (s: Script) => void): void {
  const copy = script.filter((_, i) => i !== idx);
  onChange(copy);
}

function addBeat(script: Script, onChange: (s: Script) => void): void {
  const last = script[script.length - 1];
  const start = last ? last.end : 0;
  onChange([...script, { start, end: start + 3, text: "" }]);
}

// ── Remix sub-component ─────────────────────────────────────

interface RemixAudioButtonProps {
  jobId: string;
  /** Avatar from the original job — used to seed the voice preview. */
  avatarId?: string | null;
  /** Format of the original job — used to power script auto-generation. */
  format?: UgcFormatId | null;
  /** Duration of the original job — used to power script auto-generation. */
  durationSeconds?: number | null;
  defaultAudioMode?: AudioMode;
  onSuccess?: (videoUrl: string | null) => void;
}

/**
 * Button + inline audio panel that lets the user re-record audio for an
 * already-completed job without paying for a new video generation.
 */
export function RemixAudioPanel(props: RemixAudioButtonProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const L = useUgcLight();
  const [open, setOpen] = useState(false);
  const [audioMode, setAudioMode] = useState<AudioMode>(props.defaultAudioMode ?? "overlay");
  const [script, setScript] = useState<Script>([]);

  const previewMut = useMutation({
    mutationFn: (brief?: string) => {
      if (!props.format || !props.durationSeconds) {
        throw new Error(t("ugcStudio.audio.noVideoContext"));
      }
      return ugcStudioApi.previewScript({
        format: props.format,
        durationSeconds: props.durationSeconds,
        avatarId: props.avatarId ?? null,
        userBrief: brief,
      });
    },
    onSuccess: (res) => {
      setScript(res.script);
      toast({
        title: t("ugcStudio.audio.scriptGenerated"),
        description: t("ugcStudio.audio.scriptGeneratedDesc", { count: res.script.length }),
      });
    },
    onError: (err: Error) =>
      toast({
        title: t("ugcStudio.audio.generateScriptFailed"),
        description: err.message,
        variant: "destructive",
      }),
  });

  const remixMut = useMutation({
    mutationFn: () => {
      if (audioMode === "native") throw new Error(t("ugcStudio.audio.selectAudioMode"));
      if (script.length === 0) throw new Error("Script cannot be empty");
      return ugcStudioApi.remixAudio(props.jobId, { script, audioMode });
    },
    onSuccess: (res) => {
      setOpen(false);
      toast({
        title: t("ugcStudio.audio.audioRemixed"),
        description: t("ugcStudio.audio.audioRemixedDesc"),
      });
      props.onSuccess?.(res.videoUrl);
    },
    onError: (err: Error) =>
      toast({
        title: t("ugcStudio.audio.remixFailed"),
        description: err.message,
        variant: "destructive",
      }),
  });

  if (!open) {
    return (
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        data-testid="remix-audio-toggle"
      >
        <Mic2 className="mr-2 h-4 w-4" /> {t("ugcStudio.audio.remixButton")}
      </Button>
    );
  }

  return (
    <div
      className={`space-y-3 rounded-lg border p-4 ${L ? "border-gray-200 bg-gray-50" : "border-zinc-800 bg-zinc-950/40"}`}
    >
      <div className="flex items-center justify-between">
        <Label className={`text-sm font-semibold ${L ? "text-gray-900" : "text-zinc-100"}`}>
          {t("ugcStudio.audio.remixLabel")}
        </Label>
        <div className="flex items-center gap-2">
          <VoicePreviewButton avatarId={props.avatarId ?? null} />
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setOpen(false)}
            className={`text-xs ${L ? "text-gray-500" : "text-zinc-500"}`}
          >
            {t("ugcStudio.audio.cancel")}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {AUDIO_MODE_OPTIONS.filter((o) => o.id !== "native").map((opt) => {
          const Icon = opt.icon;
          const isActive = audioMode === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => setAudioMode(opt.id)}
              className={[
                "flex items-start gap-2 rounded-md border p-2 text-left transition-colors",
                isActive
                  ? "border-orange-500 bg-orange-500/10"
                  : L
                    ? "border-gray-200 bg-gray-50 hover:border-gray-300"
                    : "border-zinc-800 bg-zinc-900/40 hover:border-zinc-700",
              ].join(" ")}
            >
              <Icon
                className={`h-4 w-4 shrink-0 ${isActive ? "text-orange-400" : L ? "text-gray-400" : "text-zinc-400"}`}
              />
              <span className="text-xs">{t(opt.labelKey)}</span>
            </button>
          );
        })}
      </div>

      <ScriptComposer
        value={script}
        onChange={setScript}
        onAutoFill={(brief) => previewMut.mutate(brief)}
        isAutoFilling={previewMut.isPending}
        videoSeconds={props.durationSeconds ?? 15}
      />

      <Button
        type="button"
        size="sm"
        onClick={() => remixMut.mutate()}
        disabled={remixMut.isPending}
        className="w-full"
        data-testid="remix-audio-submit"
      >
        {remixMut.isPending ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Mic2 className="mr-2 h-4 w-4" />
        )}
        {remixMut.isPending ? t("ugcStudio.audio.remixing") : t("ugcStudio.audio.generateNewAudio")}
      </Button>
    </div>
  );
}
